import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  listUsers,
  updateUserRole,
  updateUserStatus,
  createUserProfileViaFunction,
  ROLES,
} from "../../../services/users";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { RefreshCw, UserPlus, Shield, ShieldOff, Clock } from "lucide-react";

export default function UserManagement() {
  const { profile, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadUsers = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const userList = await listUsers();
      setUsers(userList);
    } catch (err) {
      setError("Failed to load users: " + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleRoleChange = async (uid, newRole) => {
    if (!isSuperAdmin) {
      alert("Only super-admins can change roles");
      return;
    }
    try {
      await updateUserRole(uid, newRole);
      await loadUsers(true);
    } catch (err) {
      alert("Failed to update role: " + err.message);
    }
  };

  const handleToggleDisabled = async (uid, currentlyDisabled) => {
    if (!isSuperAdmin) {
      alert("Only super-admins can enable/disable users");
      return;
    }
    try {
      // Toggle the status - if currently disabled, set to active (enabled)
      const newStatus = currentlyDisabled ? "active" : "suspended";
      await updateUserStatus(uid, newStatus);
      await loadUsers(true);
    } catch (err) {
      alert("Failed to update user status: " + err.message);
    }
  };

  const handleCreateProfile = async (uid, email) => {
    try {
      await createUserProfileViaFunction(uid, { email, role: ROLES.USER });
      await loadUsers(true);
    } catch (err) {
      alert("Failed to create profile: " + err.message);
    }
  };

  const getRoleBadgeVariant = (role) => {
    if (role === "super-admin") return "purple";
    if (role === "admin") return "default";
    return "secondary";
  };

  const formatLastSignIn = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading users...
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  // Split users into those with profiles and those without
  const usersWithProfile = users.filter((u) => u.hasProfile);
  const usersWithoutProfile = users.filter((u) => !u.hasProfile);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              {users.length} user{users.length !== 1 ? "s" : ""} in Firebase Auth
              {usersWithoutProfile.length > 0 && (
                <span className="text-amber-600 ml-2">
                  ({usersWithoutProfile.length} without profile)
                </span>
              )}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadUsers(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Users with profiles */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Auth Status</TableHead>
              <TableHead>Last Sign-In</TableHead>
              {isSuperAdmin && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersWithProfile.map((u) => (
              <TableRow key={u.uid} className={u.disabled ? "opacity-60" : ""}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                      {u.email?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.email}</span>
                        {u.uid === profile?.uid && (
                          <span className="text-xs text-muted-foreground">(you)</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {u.displayName || "No display name"}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {isSuperAdmin && u.uid !== profile?.uid ? (
                    <select
                      value={u.role || ROLES.USER}
                      onChange={(e) => handleRoleChange(u.uid, e.target.value)}
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value={ROLES.USER}>user</option>
                      <option value={ROLES.ADMIN}>admin</option>
                      <option value={ROLES.SUPER_ADMIN}>super-admin</option>
                    </select>
                  ) : (
                    <Badge variant={getRoleBadgeVariant(u.role)}>
                      {u.role || "user"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {u.disabled ? (
                    <Badge variant="destructive" className="gap-1">
                      <ShieldOff className="h-3 w-3" />
                      Disabled
                    </Badge>
                  ) : (
                    <Badge variant="success" className="gap-1">
                      <Shield className="h-3 w-3" />
                      Active
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatLastSignIn(u.lastSignInTime)}
                  </div>
                </TableCell>
                {isSuperAdmin && (
                  <TableCell>
                    {u.uid !== profile?.uid && (
                      <Button
                        variant={u.disabled ? "default" : "destructive"}
                        size="sm"
                        onClick={() => handleToggleDisabled(u.uid, u.disabled)}
                      >
                        {u.disabled ? "Enable" : "Disable"}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Users without profiles */}
        {usersWithoutProfile.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-medium text-amber-600 flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Auth Users Without Profile
            </h3>
            <p className="text-sm text-muted-foreground">
              These users can sign in but don't have a profile yet. Create a profile to assign roles.
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Auth Status</TableHead>
                  <TableHead>Last Sign-In</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersWithoutProfile.map((u) => (
                  <TableRow key={u.uid} className={u.disabled ? "opacity-60" : ""}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center text-sm font-medium text-amber-700">
                          {u.email?.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium">{u.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {u.disabled ? (
                        <Badge variant="destructive" className="gap-1">
                          <ShieldOff className="h-3 w-3" />
                          Disabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Shield className="h-3 w-3" />
                          Active (No Profile)
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatLastSignIn(u.lastSignInTime)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleCreateProfile(u.uid, u.email)}
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          Create Profile
                        </Button>
                        {isSuperAdmin && (
                          <Button
                            variant={u.disabled ? "outline" : "destructive"}
                            size="sm"
                            onClick={() => handleToggleDisabled(u.uid, u.disabled)}
                          >
                            {u.disabled ? "Enable" : "Disable"}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
